import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, UserCog, Wallet, Trophy, FileBarChart, HelpingHand, FileText, Gift, Sliders, Star, CreditCard, Banknote, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import LangToggle from "@/components/LangToggle";

const links = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, testId: "admin-nav-dashboard" },
  { to: "/admin/customers", icon: Users, label: "Customers", testId: "admin-nav-customers" },
  { to: "/admin/agents", icon: UserCog, label: "Agents", testId: "admin-nav-agents" },
  { to: "/admin/policies", icon: FileText, label: "Policies", testId: "admin-nav-policies" },
  { to: "/admin/lucky-draw", icon: Trophy, label: "Lucky Draw", testId: "admin-nav-luckydraw" },
  { to: "/admin/benefits/approvals", icon: Gift, label: "Benefit Approvals", testId: "admin-nav-benefit-approvals" },
  { to: "/admin/benefits/agent-share", icon: Star, label: "Agent Share", testId: "admin-nav-agent-share" },
  { to: "/admin/benefits/settings", icon: Sliders, label: "Benefit Settings", testId: "admin-nav-benefit-settings" },
  { to: "/admin/finance", icon: HelpingHand, label: "Finance", testId: "admin-nav-finance" },
  { to: "/admin/otp-wallet", icon: Wallet, label: "OTP Wallet", testId: "admin-nav-otp-wallet" },
  { to: "/admin/subscription", icon: CreditCard, label: "Subscription", testId: "admin-nav-subscription" },
  { to: "/admin/payment-settings", icon: Banknote, label: "Payment Settings", testId: "admin-nav-payment-settings" },
  { to: "/admin/reports", icon: FileBarChart, label: "Reports", testId: "admin-nav-reports" },
];

export default function AdminLayout() {
  const { user, tenant, logout } = useAuth();
  const nav = useNavigate();
  const onLogout = () => { logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      <header className="bg-tenant-gradient text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gold-grad grid place-items-center text-tenant-primary font-extrabold">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="font-extrabold tracking-tight text-base">{tenant?.brand_name || "Admin Panel"}</div>
              <div className="text-[11px] opacity-80">Tenant Admin · {user?.full_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LangToggle />
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 text-[11px] font-bold uppercase tracking-wider">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-2 sm:px-6 pt-3">
        <nav className="flex gap-1 overflow-x-auto scrollbar-hide bg-white rounded-2xl p-1.5 card-shadow border border-slate-100">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testId}
              className={({ isActive }) =>
                `min-w-[120px] inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap tap-scale ${
                  isActive ? "bg-tenant-primary text-white shadow-md" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <l.icon className="w-4 h-4" />
              {l.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 page-enter">
        <Outlet />
      </main>
    </div>
  );
}
